import { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useLegalCaseDetail, useUpdateLegalCaseStatus, useVerifyLegalDocument,
        useAssignAdvocateToCase,useGeneratePOA, useUploadBailDocument,} from "../../../../hooks/useLegalQueries.js";
import { CASE_STEP, BAIL_PATH, useStepIndex } from "./legalCaseSteps";
import { LegalDocPreviewModal, UploadLegalDocsModal, Badge, StepCard, StepHeader, FileDropZone,
        BailPhaseContent, PhaseTabBar, SectionCard, InfoRow, FieldLabel, PriorityBadge, FlagBadge
    } from "./AdvocateHelpers.jsx";
import { Avatar, docBadge,formatDate,Modal,} from "../../../../Component/Workshop/UI/index";
import { BackIcon, DOC_ICONS, docMeta, EyeIcon, DownloadIcon,} from "../../../../Component/Workshop/UI/icons";

const TABS = [
  { key: "overview", label: "Case Details" },
  { key: "documents", label: "Documents" },
  { key: "advocate", label: "Advocate & POA" },
  { key: "bail", label: "Bail Process" },
  { key: "timeline", label: "Timeline" },
];

const STEP_LABELS = {
  [CASE_STEP.CASE_CREATED]: "Case Created",
  [CASE_STEP.DOCS_PENDING]: "Documents Pending",
  [CASE_STEP.DOCS_VERIFIED]: "Documents Verified",
  [CASE_STEP.ADVOCATE_ASSIGNED]: "Advocate Assigned",
  [CASE_STEP.POA_GENERATED]: "POA Generated",
  [CASE_STEP.TEMP_BAIL_GRANTED]: "Temp. Bail Granted",
  [CASE_STEP.DRIVER_BAIL_PETITION_FILED]: "Driver Bail Petition",
  [CASE_STEP.DRIVER_DOCS_VERIFIED_BY_POLICE]: "Docs Verified (Police)",
  [CASE_STEP.IO_REPORT_UPLOADED]: "IO Report Uploaded",
  [CASE_STEP.DRIVER_ORDER_CREATED]: "Driver Order Created",
  [CASE_STEP.BAILER_DETAILS_ADDED]: "Bailer Details Added",
  [CASE_STEP.DRIVER_BAIL_DONE]: "Driver Bail Done",
  [CASE_STEP.VEHICLE_BAIL_PETITION_FILED]: "Vehicle Bail Petition",
  [CASE_STEP.VEHICLE_ORDER_CREATED]: "Vehicle Order Created",
  [CASE_STEP.VEHICLE_RELEASE_ORDER]: "Vehicle Release Order",
  [CASE_STEP.VEHICLE_BAIL_DONE]: "Vehicle Bail Done",
  [CASE_STEP.VEHICLE_HANDOVER_DONE]: "Vehicle Handover",
  [CASE_STEP.CASE_CLOSED]: "Case Closed",
};
const STEP_TAB = {
  [CASE_STEP.CASE_CREATED]: "overview",
  [CASE_STEP.DOCS_PENDING]: "documents",
  [CASE_STEP.DOCS_VERIFIED]: "documents",
  [CASE_STEP.ADVOCATE_ASSIGNED]: "advocate",
  [CASE_STEP.POA_GENERATED]: "advocate",
  [CASE_STEP.TEMP_BAIL_GRANTED]: "bail",
  [CASE_STEP.DRIVER_BAIL_PETITION_FILED]: "bail",
  [CASE_STEP.DRIVER_DOCS_VERIFIED_BY_POLICE]: "bail",
  [CASE_STEP.IO_REPORT_UPLOADED]: "bail",
  [CASE_STEP.DRIVER_ORDER_CREATED]: "bail",
  [CASE_STEP.BAILER_DETAILS_ADDED]: "bail",
  [CASE_STEP.DRIVER_BAIL_DONE]: "bail",
  [CASE_STEP.VEHICLE_BAIL_PETITION_FILED]: "bail",
  [CASE_STEP.VEHICLE_ORDER_CREATED]: "bail",
  [CASE_STEP.VEHICLE_RELEASE_ORDER]: "bail",
  [CASE_STEP.VEHICLE_BAIL_DONE]: "bail",
  [CASE_STEP.VEHICLE_HANDOVER_DONE]: "bail",
  [CASE_STEP.CASE_CLOSED]: "bail",
};

const BAIL_FLOW_STEPS = new Set([
  CASE_STEP.TEMP_BAIL_GRANTED,
  CASE_STEP.DRIVER_BAIL_PETITION_FILED,
  CASE_STEP.DRIVER_DOCS_VERIFIED_BY_POLICE,
  CASE_STEP.IO_REPORT_UPLOADED,
  CASE_STEP.DRIVER_ORDER_CREATED,
  CASE_STEP.BAILER_DETAILS_ADDED,
  CASE_STEP.DRIVER_BAIL_DONE,
  CASE_STEP.VEHICLE_BAIL_PETITION_FILED,
  CASE_STEP.VEHICLE_ORDER_CREATED,
  CASE_STEP.VEHICLE_RELEASE_ORDER,
  CASE_STEP.VEHICLE_BAIL_DONE,
  CASE_STEP.VEHICLE_HANDOVER_DONE,
  CASE_STEP.CASE_CLOSED,
]);

function stepLabel(step) {
    return STEP_LABELS[step] || step.replace(/_/g, " ");
}

const PILL_VARIANTS = {
  default: {
    background: "rgba(255,255,255,0.06)",
    color: "var(--wd-t2)",
    border: "1px solid var(--wd-border)",
  },
  info: {
    background: "rgba(33,166,233,0.12)",
    color: "#38bdf8",
    border: "1px solid rgba(33,166,233,0.28)",
  },
  warning: {
    background: "rgba(234,179,8,0.12)",
    color: "#facc15",
    border: "1px solid rgba(234,179,8,0.28)",
  },
  success: {
    background: "rgba(34,197,94,0.12)",
    color: "#4ade80",
    border: "1px solid rgba(34,197,94,0.25)",
  },
  danger: {
    background: "rgba(239,68,68,0.1)",
    color: "#f87171",
    border: "1px solid rgba(239,68,68,0.25)",
  },
};

function pillStyle(variant = "default") {
  const v = PILL_VARIANTS[variant] || PILL_VARIANTS.default;
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 11px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: "nowrap",
    ...v,
  };
}

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)} d ago`;
}

export default function AdvocateCaseDetailPage() {
  const { caseId: caseNo } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    data: caseRecord,
    isLoading,
    isError,
    refetch,
  } = useLegalCaseDetail(caseNo);

  const updateStatus = useUpdateLegalCaseStatus(caseNo);

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [directStatus, setDirectStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [statusError, setStatusError] = useState("");

  const closeStatusModal = () => {
    setStatusModalOpen(false);
    setDirectStatus("");
    setStatusReason("");
    setStatusError("");
  };

  const handleDirectStatusUpdate = async () => {
    if (!directStatus) return;
    if (!statusReason.trim()) {
      setStatusError("Please provide a reason for this status change.");
      return;
    }
    setStatusError(null);
    try {
      await updateStatus.mutateAsync({
        caseStep: directStatus,
        description: statusReason.trim(),
        isDirectUpdate: true,
      });
      await refetch();
      closeStatusModal();
    } catch (err) {
      console.error("Direct status update failed", err);
      setStatusError("Failed to update status. Please retry.");
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="qs-state-box">
        <div className="qs-spinner" />
        <span>Loading case…</span>
      </div>
    );
  }
  if (isError || !caseRecord) {
    return (
      <div className="wd-card" style={{ padding: 32 }}>
        Case not found.
      </div>
    );
  }

  return (
    <div className="wd-page">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            className="wd-btn wd-btn-ghost wd-btn-sm"
            onClick={() =>
              navigate("/admin/quick-services", {
                state: {
                  activeTab: "advocate",
                  highlightedCase: caseNo,
                },
              })
            }
          >
            <BackIcon /> Back
          </button>
          <span style={{ color: "var(--wd-t3)", fontSize: 13 }}>/</span>
          <span style={{ color: "var(--wd-t3)", fontSize: 13 }}>
            Legal cases
          </span>
          <span style={{ color: "var(--wd-t3)", fontSize: 13 }}>/</span>
          <span
            style={{
              fontFamily: "var(--wd-mono)",
              fontSize: 13,
              color: "var(--wd-blue)",
              fontWeight: 600,
            }}
          >
            {caseRecord.caseNo}
          </span>
          {caseRecord.accidentCaseNo && (
            <>
              <span style={{ color: "var(--wd-t3)", fontSize: 13 }}>·</span>
              <button
                className="wd-btn wd-btn-ghost wd-btn-sm"
                onClick={() =>
                  navigate(`/admin/cases/${caseRecord.accidentCaseNo}`, {
                    state: { activeTab: "On-Spot Services" },
                  })
                }
              >
                ↗ Case {caseRecord.accidentCaseNo}
              </button>
            </>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            className="wd-btn wd-btn-primary wd-btn-sm"
            onClick={() => setStatusModalOpen(true)}
          >
            Update Status
          </button>

          <button
            className="wd-btn wd-btn-ghost wd-btn-sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
            title="Force refresh if data seems stale"
          >
            <span
              style={{
                display: "inline-block",
                animation: isRefreshing
                  ? "wd-spin 0.8s linear infinite"
                  : "none",
              }}
            >
              🔄
            </span>
            {isRefreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="wd-card" style={{ padding: 20 }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          {/* Left: avatar + identity */}
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <Avatar name={caseRecord.customerName || "?"} size={48} />
            <div>
              {/* Code + badges row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--wd-mono)",
                    fontSize: 18,
                    fontWeight: 800,
                    color: "var(--wd-blue)",
                  }}
                >
                  {caseRecord.caseNo}
                </span>
                {/* Case type pill */}
                <span style={pillStyle("default")}>
                  {caseRecord.caseType || "Legal case"}
                </span>
                {/* Step / status pill */}
                <span
                  style={pillStyle(
                    caseRecord.caseStep === CASE_STEP.CASE_CLOSED
                      ? "success"
                      : "info",
                  )}
                >
                  {(caseRecord.caseStep || "").replace(/_/g, " ") ||
                    "Case created"}
                </span>
                {/* Priority pill */}
                {caseRecord.priority && (
                  <span
                    style={pillStyle(
                      caseRecord.priority === "CRITICAL" ||
                        caseRecord.priority === "HIGH"
                        ? "danger"
                        : caseRecord.priority === "MEDIUM"
                          ? "warning"
                          : "default",
                    )}
                  >
                    {caseRecord.priority}
                  </span>
                )}
                {/* Injury / death flags */}
                {caseRecord.injuryReported && (
                  <span style={pillStyle("warning")}>🤕 Injury</span>
                )}
                {caseRecord.deathReported && (
                  <span style={pillStyle("danger")}>🚨 Death reported</span>
                )}
              </div>

              {/* Sub-line 1: name · phone · plate — name & plate highlighted */}
              <div
                style={{
                  marginTop: 4,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flexWrap: "wrap",
                  fontSize: 15,
                }}
              >
                {caseRecord.customerName && (
                  <span style={{ color: "var(--wd-t1)", fontWeight: 700 }}>
                    {caseRecord.customerName}
                  </span>
                )}
                {caseRecord.customerPhone && (
                  <>
                    <span style={{ color: "var(--wd-t3)" }}>·</span>
                    <span style={{ color: "var(--wd-t2)" }}>
                      {caseRecord.customerPhone}
                    </span>
                  </>
                )}
                {caseRecord.vehicleNumber && (
                  <>
                    <span style={{ color: "var(--wd-t3)" }}>·</span>
                    <span
                      style={{
                        fontFamily: "var(--wd-mono)",
                        fontWeight: 700,
                        color: "var(--wd-blue)",
                        fontSize: 17,
                      }}
                    >
                      {caseRecord.vehicleNumber}
                    </span>
                  </>
                )}
              </div>

              {/* Sub-line 2: location · time */}
              <div
                style={{ marginTop: 2, color: "var(--wd-t3)", fontSize: 12 }}
              >
                {caseRecord.incidentLocation && (
                  <>📍 {caseRecord.incidentLocation}</>
                )}
                {timeAgo(caseRecord.createdAt || caseRecord.caseCreatedAt) &&
                  ` · ${timeAgo(caseRecord.createdAt || caseRecord.caseCreatedAt)}`}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Case Progress checklist — own card, matches CaseProgressChecklist.jsx ── */}
      <div style={{ marginTop: 16 }}>
        <CaseProgressChecklist caseRecord={caseRecord} onJump={setTab} />
      </div>

      {/* ── Tabs ── */}
      <div className="qs-tabs" style={{ marginTop: 16 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`qs-tab ${tab === t.key ? "qs-tab--active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        {tab === "overview" && <OverviewTab caseRecord={caseRecord} />}
        {tab === "documents" && <DocumentsTab caseRecord={caseRecord} />}
        {tab === "advocate" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <AdvocateAssignTab caseRecord={caseRecord} />
            {caseRecord.advocateName && <POATab caseRecord={caseRecord} />}
          </div>
        )}
        {tab === "bail" &&
          (caseRecord.poa ? (
            <BailProcessTab caseRecord={caseRecord} />
          ) : (
            <div
              className="wd-card"
              style={{
                padding: 24,
                textAlign: "center",
                color: "var(--wd-t3)",
              }}
            >
              Generate the POA in the "Advocate &amp; POA" tab before starting
              the bail process.
            </div>
          ))}
        {tab === "timeline" && <TimelineTab caseRecord={caseRecord} />}
      </div>

      <Modal
        open={statusModalOpen}
        onClose={closeStatusModal}
        title="Update Case Status"
        footer={
          <>
            <button className="wd-btn wd-btn-ghost" onClick={closeStatusModal}>
              Cancel
            </button>
            <button
              className="wd-btn wd-btn-primary"
              onClick={handleDirectStatusUpdate}
              disabled={!directStatus || updateStatus.isPending}
            >
              {updateStatus.isPending ? "Updating…" : "Update"}
            </button>
          </>
        }
      >
        <div>
          <label className="wd-form-label">New Status</label>
          <select
            className="wd-input wd-select"
            value={directStatus}
            onChange={(e) => {
              setDirectStatus(e.target.value);
              setStatusError(null);
            }}
          >
            <option value="">Select status...</option>
            {Object.entries(STEP_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>

        {/* ADDED: heads-up when skipping straight into a bail sub-step
            without going through the normal fork/flow — bailPath and the
            related JSON fields won't be populated by this shortcut. */}
        {directStatus &&
          BAIL_FLOW_STEPS.has(directStatus) &&
          !caseRecord.bailPath && (
            <div
              style={{
                marginTop: 10,
                padding: "8px 12px",
                borderRadius: 8,
                fontSize: 12,
                background: "rgba(234,179,8,.08)",
                border: "1px solid rgba(234,179,8,.2)",
                color: "var(--wd-yellow)",
              }}
            >
              ⚠️ This skips the driver/vehicle bail setup. The Bail Process tab
              may re-ask the temp-bail question until that path is set.
            </div>
          )}

        <div style={{ marginTop: 12 }}>
          <label className="wd-form-label">Reason *</label>
          <textarea
            className="wd-input"
            rows={3}
            style={{ resize: "vertical" }}
            value={statusReason}
            onChange={(e) => {
              setStatusReason(e.target.value);
              setStatusError(null);
            }}
            placeholder="Explain why the status is being changed directly (required)..."
          />
        </div>

        {statusError && (
          <div style={{ marginTop: 8, color: "var(--wd-red)", fontSize: 12 }}>
            {statusError}
          </div>
        )}
      </Modal>
    </div>
  );
}

function CaseProgressChecklist({ caseRecord, onJump }) {
  const scrollRef = useRef(null);
  const { order, idx } = useStepIndex(caseRecord);
  const safeIdx = idx === -1 ? 0 : idx;
  const doneCount = order.filter((step) =>
    computeStepDone(step, caseRecord, idx, order),
  ).length;

  // Auto-scroll the active step to center, same pattern as CaseProgressChecklist.jsx
  useEffect(() => {
    const wrap = scrollRef.current;
    const el = wrap?.querySelector(`[data-step="${safeIdx}"]`);
    if (!wrap || !el) return;
    const left = el.offsetLeft - wrap.offsetWidth / 2 + el.offsetWidth / 2;
    wrap.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  }, [safeIdx]);
  function computeStepDone(step, caseRecord, idx, order) {
    if (caseRecord.directlyClosed) {
      return [CASE_STEP.CASE_CREATED, CASE_STEP.CASE_CLOSED].includes(step);
    }

    const docs = caseRecord.documents || [];
    const i = order.indexOf(step);
    const positionalDone = i < idx;

    switch (step) {
      case CASE_STEP.CASE_CREATED:
        return true; // if the case exists, it's created

      case CASE_STEP.DOCS_PENDING:
        // "pending" step is complete once at least one doc has been uploaded
        return docs.length > 0;

      case CASE_STEP.DOCS_VERIFIED: {
        // only "verified" if there ARE docs and none are still pending/rejected
        if (docs.length === 0) return false;
        return docs.every((d) => ["APPROVED", "VERIFIED"].includes(d.status));
      }

      case CASE_STEP.ADVOCATE_ASSIGNED:
        return !!caseRecord.advocateName;

      case CASE_STEP.POA_GENERATED:
        return !!caseRecord.poa;

      default:
        // bail-process sub-steps: no independently verifiable field exposed
        // here, so fall back to the caseStep pointer
        return positionalDone;
    }
  }

  return (
    <div className="wd-card" style={{ padding: "14px 20px" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 14,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-t1)" }}>
          Case Progress
        </span>
        <span style={{ fontSize: 11, color: "var(--wd-t3)" }}>
          {doneCount} / {order.length} completed
        </span>
      </div>

      {/* Scrollable step track */}
      <div
        ref={scrollRef}
        style={{
          overflowX: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: "var(--wd-border-md) transparent",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            minWidth: "max-content",
            padding: "4px 2px 8px",
          }}
        >
          {order.map((step, i) => {
            const isDone = computeStepDone(step, caseRecord, idx, order);
            const isSkipped =
              caseRecord.directlyClosed &&
              step !== CASE_STEP.CASE_CREATED &&
              step !== CASE_STEP.CASE_CLOSED;
            const isActive = !isDone && !isSkipped && i === idx;
            const state = isDone
              ? "done"
              : isSkipped
                ? "skipped"
                : isActive
                  ? "active"
                  : "pending";

            const styles = {
              done: {
                dot: "var(--wd-green)",
                border: "var(--wd-green)",
                label: "var(--wd-t1)",
                sub: "var(--wd-t3)",
                subText: "✓",
              },
              active: {
                dot: "var(--wd-blue)",
                border: "var(--wd-blue)",
                label: "var(--wd-blue)",
                sub: "var(--wd-blue)",
                subText: "In progress",
              },
              pending: {
                dot: "var(--wd-hover)",
                border: "var(--wd-border-md)",
                label: "var(--wd-t3)",
                sub: "var(--wd-orange)",
                subText: "Pending",
              },
              skipped: {
                dot: "var(--wd-hover)",
                border: "var(--wd-border-md)",
                label: "var(--wd-t3)",
                sub: "var(--wd-t3)",
                subText: "Skipped",
              },
            }[state];

            const jumpTarget = STEP_TAB[step] || "overview";

            return (
              <div
                key={step}
                data-step={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  flex: i < order.length - 1 ? 1 : 0,
                }}
              >
                <div
                  onClick={() => onJump?.(jumpTarget)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && onJump?.(jumpTarget)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 5,
                    cursor: "pointer",
                  }}
                >
                  {/* Dot */}
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: styles.dot,
                      border: `2px solid ${styles.border}`,
                      boxShadow: isActive
                        ? "0 0 0 4px rgba(59,130,246,.15)"
                        : "none",
                    }}
                  >
                    {isDone ? (
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="3"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: isActive ? "#fff" : "var(--wd-border-md)",
                        }}
                      />
                    )}
                  </div>

                  {/* Label */}
                  <div style={{ textAlign: "center", minWidth: 80 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: ".04em",
                        lineHeight: 1.3,
                        color: styles.label,
                      }}
                    >
                      {stepLabel(step)}
                    </div>
                    <div
                      style={{ fontSize: 9, marginTop: 2, color: styles.sub }}
                    >
                      {styles.subText}
                    </div>
                  </div>
                </div>

                {/* Connector */}
                {i < order.length - 1 && (
                  <div
                    style={{
                      flex: 1,
                      height: 2,
                      minWidth: 20,
                      background: isSkipped
                        ? "var(--wd-border-md)"
                        : isDone
                        ? "var(--wd-green)"
                        : "var(--wd-border-md)",
                      margin: "0 6px",
                      marginBottom: 28,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════ Step 1 — Case Details (read-only) ══════════════════ */

function OverviewTab({ caseRecord }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      <SectionCard title="Vehicle & Customer">
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
        >
          <InfoRow
            label="Vehicle Number"
            value={caseRecord.vehicleNumber}
            mono
          />
          <InfoRow label="Case Type" value={caseRecord.caseType || "—"} />
          <InfoRow label="Customer Name (RC)" value={caseRecord.customerName} />
          <InfoRow
            label="Mobile Number"
            value={caseRecord.customerPhone}
            mono
          />
          <InfoRow label="Email" value={caseRecord.customerEmail || "—"} />
          <InfoRow
            label="Incident Location"
            value={caseRecord.incidentLocation || "—"}
          />
        </div>
      </SectionCard>

      <SectionCard title="Accident Details">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <InfoRow label="Date" value={formatDate(caseRecord.accidentDate)} />
            <InfoRow label="Time" value={caseRecord.accidentTime || "—"} />
          </div>
          {caseRecord.caseDescription && (
            <div>
              <FieldLabel>Description</FieldLabel>
              <p
                style={{
                  fontSize: 13,
                  color: "var(--wd-t2)",
                  lineHeight: 1.6,
                  margin: "4px 0 0",
                }}
              >
                {caseRecord.caseDescription}
              </p>
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard title="FIR & Authority">
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
        >
          <InfoRow
            label={
              caseRecord.authorityType === "COURT"
                ? "Court Name"
                : "Police Station"
            }
            value={caseRecord.authorityName || "—"}
          />
          <InfoRow
            label="FIR Number"
            value={caseRecord.firNumber || "—"}
            mono
          />
        </div>
        {caseRecord.firCopyUrl && (
          <a
            href={caseRecord.firCopyUrl}
            target="_blank"
            rel="noreferrer"
            className="wd-btn wd-btn-ghost wd-btn-sm"
            style={{ marginTop: 10 }}
          >
            ↗ View FIR Copy
          </a>
        )}
        {caseRecord.accidentMedia?.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <FieldLabel>Accident Photos / Videos</FieldLabel>
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 6,
              }}
            >
              {caseRecord.accidentMedia.map((m, i) => (
                <a
                  key={i}
                  href={m.url}
                  target="_blank"
                  rel="noreferrer"
                  className="wd-btn wd-btn-ghost wd-btn-sm"
                  style={{ fontSize: 11 }}
                >
                  📎 {m.name}
                </a>
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Severity & Notes">
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <PriorityBadge priority={caseRecord.priority} />
          {caseRecord.injuryReported && (
            <FlagBadge
              color="var(--wd-orange)"
              icon="🤕"
              label="Injury reported"
            />
          )}
          {caseRecord.deathReported && (
            <FlagBadge color="var(--wd-red)" icon="🚨" label="Death reported" />
          )}
        </div>
        {caseRecord.adminNotes && (
          <div>
            <FieldLabel>Admin Notes</FieldLabel>
            <p
              style={{
                fontSize: 13,
                color: "var(--wd-t2)",
                lineHeight: 1.6,
                margin: "4px 0 0",
              }}
            >
              {caseRecord.adminNotes}
            </p>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

/* ══════════════════ Step 2 — Documents ══════════════════ */

const DOC_TYPE_OPTIONS = [
  "RC",
  "Insurance Policy",
  "Driving License",
  "FIR Copy",
  "Accident Photo/Video",
  "Other",
];

/* ── Documents Tab — table + preview modal + upload modal, isKAM-gated ── */
function DocumentsTab({ caseRecord }) {
  const verifyDoc = useVerifyLegalDocument(caseRecord.caseNo);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [actionError, setActionError] = useState(null);

  const docs = caseRecord.documents || [];

  // Same role gate as WorkshopTabHelpers' DocumentsTab
  const userRole = localStorage.getItem("department") || "";
  const isKAM = userRole.toLowerCase() === "kam";

  const handleApprove = async (doc) => {
    if (!doc?.id || isKAM) return;
    setActionError(null);
    try {
      await verifyDoc.mutateAsync({ docId: doc.id, status: "APPROVED" });
      setPreviewDoc(null);
    } catch (err) {
      console.error("Approve doc failed", err);
      setActionError("Approve failed. Please retry.");
    }
  };

  const handleReject = async (doc) => {
    if (!doc?.id || isKAM) return;
    setActionError(null);
    try {
      await verifyDoc.mutateAsync({
        docId: doc.id,
        status: "REJECTED",
        remarks: "Rejected from case detail",
      });
      setPreviewDoc(null);
    } catch (err) {
      console.error("Reject doc failed", err);
      setActionError("Reject failed. Please retry.");
    }
  };

  const uploadButton = (
    <button
      className="wd-btn wd-btn-primary wd-btn-sm"
      onClick={() => setUploadOpen(true)}
    >
      📎 Upload Documents
    </button>
  );

  const uploadModal = (
    <UploadLegalDocsModal
      open={uploadOpen}
      onClose={() => setUploadOpen(false)}
      caseNo={caseRecord.caseNo}
      existingDocuments={docs}
      onSuccess={() => setUploadOpen(false)}
    />
  );

  if (docs.length === 0) {
    return (
      <>
        <div className="wd-card" style={{ padding: 32 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
              padding: "20px 0",
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: 36, opacity: 0.5 }}>📄</span>
            <div
              style={{ fontSize: 14, fontWeight: 600, color: "var(--wd-t2)" }}
            >
              No documents uploaded yet
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--wd-t3)",
                maxWidth: 360,
                lineHeight: 1.6,
              }}
            >
              Upload driver, vehicle, and accident documents for this case.
            </div>
          </div>
          <div
            style={{ display: "flex", justifyContent: "center", marginTop: 16 }}
          >
            {uploadButton}
          </div>
        </div>
        {uploadModal}
      </>
    );
  }

  const pending = docs.filter((d) => d.status === "PENDING").length;
  const approved = docs.filter((d) =>
    ["APPROVED", "VERIFIED"].includes(d.status),
  ).length;
  const rejected = docs.filter((d) => d.status === "REJECTED").length;

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", gap: 12 }}>
          {[
            ["Total", docs.length, "#3b82f6"],
            ["Approved", approved, "#22c55e"],
            ["Pending", pending, "#eab308"],
            ["Rejected", rejected, "#ef4444"],
          ].map(([l, v, c]) => (
            <div
              key={l}
              style={{
                padding: "8px 16px",
                borderRadius: 10,
                background: `${c}18`,
                border: `1px solid ${c}33`,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ fontSize: 18, fontWeight: 800, color: c }}>
                {v}
              </span>
              <span style={{ fontSize: 12, color: "var(--wd-t2)" }}>{l}</span>
            </div>
          ))}
          {uploadButton}
        </div>

        {pending > 0 && (
          <div
            style={{
              display: "flex",
              gap: 10,
              padding: "10px 14px",
              background: "rgba(234,179,8,.08)",
              borderRadius: 10,
              border: "1px solid rgba(234,179,8,.2)",
            }}
          >
            <span>⚠️</span>
            <span style={{ fontSize: 12, color: "var(--wd-yellow)" }}>
              <strong>{pending} document(s)</strong> waiting for your review.
            </span>
          </div>
        )}
        {rejected > 0 && (
          <div
            style={{
              display: "flex",
              gap: 10,
              padding: "10px 14px",
              background: "rgba(239,68,68,.08)",
              borderRadius: 10,
              border: "1px solid rgba(239,68,68,.2)",
            }}
          >
            <span>❌</span>
            <span style={{ fontSize: 12, color: "var(--wd-red)" }}>
              <strong>{rejected} document(s)</strong> rejected — needs
              re-upload.
            </span>
          </div>
        )}

        <div className="wd-card">
          <div className="wd-table-wrap">
            <table className="wd-table">
              <thead>
                <tr>
                  <th>TYPE</th>
                  <th>STATUS</th>
                  <th>UPLOADED BY</th>
                  <th>EXPIRY DATE</th>
                  <th>ULIP VERIFIED</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((doc) => {
                  const meta = docMeta(doc.type);
                  return (
                    <tr
                      key={doc.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => setPreviewDoc(doc)}
                    >
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <div
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 8,
                              flexShrink: 0,
                              background: `${meta.color}18`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 16,
                            }}
                          >
                            {meta.icon}
                          </div>
                          <span style={{ fontWeight: 500, fontSize: 13 }}>
                            {(doc.type || "").replace(/_/g, " ")}
                          </span>
                        </div>
                      </td>

                      <td>
                        <Badge variant={docBadge(doc.status)}>
                          {doc.status || "PENDING"}
                        </Badge>
                      </td>

                      <td style={{ fontSize: 12, color: "var(--wd-t3)" }}>
                        {doc.uploadedBy || "—"}
                      </td>

                      <td
                        style={{
                          fontSize: 12,
                          color: doc.ulipExpiryDate
                            ? (() => {
                                const diff =
                                  (new Date(doc.ulipExpiryDate) - new Date()) /
                                  (1000 * 60 * 60 * 24);
                                return diff < 0
                                  ? "var(--wd-red)"
                                  : "var(--wd-green)";
                              })()
                            : "var(--wd-t3)",
                        }}
                      >
                        {doc.ulipExpiryDate
                          ? formatDate(doc.ulipExpiryDate)
                          : "—"}
                      </td>

                      <td style={{ fontSize: 12, color: "var(--wd-t3)" }}>
                        {doc.ulipVerified === true
                          ? "Yes"
                          : doc.ulipVerified === false
                            ? "No"
                            : "Not verified yet"}
                      </td>

                      <td onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: "flex", gap: 5 }}>
                          <button
                            className="wd-btn wd-btn-ghost wd-btn-sm"
                            onClick={() => setPreviewDoc(doc)}
                          >
                            <EyeIcon /> View
                          </button>
                          {doc.fileUrl && (
                            <a
                              href={doc.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="wd-btn wd-btn-ghost wd-btn-sm"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <DownloadIcon />
                            </a>
                          )}
                          {["PENDING", null, undefined].includes(doc.status) &&
                            !isKAM && (
                              <>
                                <button
                                  className="wd-btn wd-btn-sm"
                                  style={{
                                    background: "rgba(34,197,94,.12)",
                                    color: "var(--wd-green)",
                                    border: "none",
                                  }}
                                  onClick={() => handleApprove(doc)}
                                  disabled={verifyDoc.isPending}
                                >
                                  ✓ Approve
                                </button>
                                <button
                                  className="wd-btn wd-btn-danger wd-btn-sm"
                                  onClick={() => handleReject(doc)}
                                  disabled={verifyDoc.isPending}
                                >
                                  ✗ Reject
                                </button>
                              </>
                            )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {actionError && (
        <div style={{ margin: "8px 0", color: "var(--wd-red)", fontSize: 12 }}>
          {actionError}
        </div>
      )}

      <LegalDocPreviewModal
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
        onApprove={handleApprove}
        onReject={handleReject}
        isKAM={isKAM}
      />
      {uploadModal}
    </>
  );
}

/* ══════════════════ Step 3 — Advocate Assignment ══════════════════ */

function AdvocateAssignTab({ caseRecord }) {
  const navigate = useNavigate();
  const hasAdvocate = !!caseRecord.advocateName;

  const goToVendors = () =>
    navigate(
      `/admin/vendors?requestCode=${caseRecord.caseNo}&serviceType=advocate&tab=advocate`,
    );

  return (
    <SectionCard title="Advocate Assignment">
      {hasAdvocate ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div
              style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-t1)" }}
            >
              {caseRecord.advocateName}
            </div>
            <div style={{ fontSize: 11, color: "var(--wd-t3)" }}>
              Assigned advocate
            </div>
          </div>
          <button
            className="wd-btn wd-btn-ghost wd-btn-sm"
            onClick={goToVendors}
          >
            Change advocate
          </button>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <p style={{ fontSize: 12, color: "var(--wd-t3)", marginBottom: 10 }}>
            No advocate assigned yet.
          </p>
          <button
            className="wd-btn wd-btn-primary wd-btn-sm"
            onClick={goToVendors}
          >
            ⚖️ Select Advocate
          </button>
        </div>
      )}
    </SectionCard>
  );
}

/* ══════════════════ Step 4 — Company Rep → POA ══════════════════ */

const REP_FIELDS = [
  ["name", "Name"],
  ["fatherName", "Father's Name"],
  ["aadhar", "Aadhar Number"],
  ["pan", "PAN Number"],
  ["mobile", "Mobile Number"],
  ["email", "Email"],
  ["designation", "Designation"],
];
const AADHAR_REGEX = /^\d{12}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

function formatAadharInput(raw) {
  return raw.replace(/\D/g, "").slice(0, 12);
}

function formatPanInput(raw) {
  return raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 10);
}

function POATab({ caseRecord }) {
  const generatePOA = useGeneratePOA(caseRecord.caseNo);
  const [form, setForm] = useState(caseRecord.companyRep || {});
  const [aadharFile, setAadharFile] = useState(null);
  const [panFile, setPanFile] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const set = (k, v) => {
    let next = v;
    if (k === "aadhar") next = formatAadharInput(v);
    if (k === "pan") next = formatPanInput(v);
    setForm((f) => ({ ...f, [k]: next }));
  };

  const aadharTouched = (form.aadhar || "").length > 0;
  const panTouched = (form.pan || "").length > 0;
  const aadharValid = AADHAR_REGEX.test(form.aadhar || "");
  const panValid = PAN_REGEX.test(form.pan || "");

  const hasAadharDoc = !!aadharFile || !!caseRecord.companyRep?.aadharCardUrl;
  const hasPanDoc = !!panFile || !!caseRecord.companyRep?.panCardUrl;

  const canGenerate =
    REP_FIELDS.every(([k]) => (form[k] || "").trim().length > 0) &&
    aadharValid &&
    panValid &&
    hasAadharDoc &&
    hasPanDoc;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setSubmitError(null);
    try {
      // Sending as FormData so the aadharCard/panCard files travel alongside
      // the text fields in one request. NOTE: if useGeneratePOA's mutation
      // currently posts JSON (application/json), it will need a small
      // update on its side to accept multipart/form-data — the hook's
      // implementation isn't part of this file so that change lives there.
      const payload = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v != null) payload.append(k, v);
      });
      if (aadharFile) payload.append("aadharCard", aadharFile);
      if (panFile) payload.append("panCard", panFile);
      await generatePOA.mutateAsync(payload);
    } catch (err) {
      console.error("Generate POA failed", err);
      setSubmitError("Failed to generate POA. Please retry.");
    }
  };

  if (caseRecord.poa) {
    return (
      <SectionCard title="Power of Attorney">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 24 }}>📄</span>
          <div style={{ flex: 1 }}>
            <div
              style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-t1)" }}
            >
              POA generated for {caseRecord.companyRep?.name}
            </div>
            <div style={{ fontSize: 11, color: "var(--wd-t3)" }}>
              {caseRecord.poa.generatedAt
                ? new Date(caseRecord.poa.generatedAt).toLocaleString()
                : "—"}
            </div>
          </div>
          {/* poaUrl is currently null until real PDF generation is wired server-side (see legalController.js TODO) */}
          {caseRecord.poa.fileUrl ? (
            <a
              className="wd-btn wd-btn-ghost wd-btn-sm"
              href={caseRecord.poa.fileUrl}
              target="_blank"
              rel="noreferrer"
            >
              ↗ View POA
            </a>
          ) : (
            <span style={{ fontSize: 11, color: "var(--wd-orange)" }}>
              PDF generation pending
            </span>
          )}
        </div>

        {/* ADDED: quick links to the uploaded ID docs once POA exists */}
        {(caseRecord.companyRep?.aadharCardUrl ||
          caseRecord.companyRep?.panCardUrl) && (
          <div
            style={{
              marginTop: 14,
              paddingTop: 14,
              borderTop: "1px solid var(--wd-border)",
              display: "flex",
              gap: 10,
            }}
          >
            {caseRecord.companyRep?.aadharCardUrl && (
              <a
                className="wd-btn wd-btn-ghost wd-btn-sm"
                href={caseRecord.companyRep.aadharCardUrl}
                target="_blank"
                rel="noreferrer"
              >
                ↗ View Aadhar Card
              </a>
            )}
            {caseRecord.companyRep?.panCardUrl && (
              <a
                className="wd-btn wd-btn-ghost wd-btn-sm"
                href={caseRecord.companyRep.panCardUrl}
                target="_blank"
                rel="noreferrer"
              >
                ↗ View PAN Card
              </a>
            )}
          </div>
        )}
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Company Representative — POA Generation">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {REP_FIELDS.map(([k, label]) => {
          const isAadhar = k === "aadhar";
          const isPan = k === "pan";
          const showError =
            (isAadhar && aadharTouched && !aadharValid) ||
            (isPan && panTouched && !panValid);

          return (
            <div
              key={k}
              style={{ display: "flex", flexDirection: "column", gap: 4 }}
            >
              <FieldLabel>{label}</FieldLabel>
              <input
                className="wd-input"
                value={form[k] || ""}
                onChange={(e) => set(k, e.target.value)}
                maxLength={isAadhar ? 12 : isPan ? 10 : undefined}
                placeholder={
                  isAadhar
                    ? "12-digit Aadhar number"
                    : isPan
                      ? "ABCDE1234F"
                      : undefined
                }
                style={showError ? { borderColor: "var(--wd-red)" } : undefined}
              />
              {showError && (
                <span style={{ fontSize: 11, color: "var(--wd-red)" }}>
                  {isAadhar
                    ? "Aadhar number must be exactly 12 digits."
                    : "PAN must be in the format ABCDE1234F."}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* ADDED: Aadhar / PAN card uploads, required alongside the number fields */}
      <div
        style={{
          marginTop: 16,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
        }}
      >
        <div>
          <FieldLabel>Aadhar Card</FieldLabel>
          <div style={{ marginTop: 4 }}>
            <FileDropZone
              label="Aadhar card (image or PDF)"
              file={aadharFile}
              existingUrl={caseRecord.companyRep?.aadharCardUrl}
              onFile={setAadharFile}
              onClear={() => setAadharFile(null)}
              dragId="rep-aadhar-input"
            />
          </div>
        </div>
        <div>
          <FieldLabel>PAN Card</FieldLabel>
          <div style={{ marginTop: 4 }}>
            <FileDropZone
              label="PAN card (image or PDF)"
              file={panFile}
              existingUrl={caseRecord.companyRep?.panCardUrl}
              onFile={setPanFile}
              onClear={() => setPanFile(null)}
              dragId="rep-pan-input"
            />
          </div>
        </div>
      </div>

      {submitError && (
        <div style={{ marginTop: 10, color: "var(--wd-red)", fontSize: 12 }}>
          {submitError}
        </div>
      )}

      <div
        style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}
      >
        <button
          className="wd-btn wd-btn-primary wd-btn-sm"
          disabled={!canGenerate || generatePOA.isPending}
          onClick={handleGenerate}
        >
          {generatePOA.isPending ? "Generating…" : "📝 Generate POA"}
        </button>
      </div>
    </SectionCard>
  );
}

/* ══════════════════ Shared: edit-toggle summary row ══════════════════ */

function StepSummary({ text, onEdit }) {
  return (
    <div
      style={{
        marginTop: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
      }}
    >
      <span style={{ fontSize: 13, color: "var(--wd-t2)" }}>{text}</span>
      <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={onEdit}>
        ✎ Edit
      </button>
    </div>
  );
}

/* ══════════════════ Steps 5–11 — Bail Process (WorkshopTab-style phase tabs) ══════════════════ */

const BAIL_PHASE_META = {
  driver: { label: "Driver Bail" },
  vehicle: { label: "Vehicle Bail" },
};

function BailProcessTab({ caseRecord }) {
  const updateStatus = useUpdateLegalCaseStatus(caseRecord.caseNo);
  const { order, idx } = useStepIndex(caseRecord);
  const stepState = (status) => {
    const i = order.indexOf(status);
    if (i < idx) return "done";
    if (i === idx) return "active";
    return "locked";
  };
  const advance = (caseStep, extra = {}) =>
    updateStatus.mutate({ caseStep, ...extra });

  const driverSteps = useDriverBailSteps({ caseRecord, stepState, advance });
  const vehicleSteps = useVehicleBailSteps({ caseRecord, stepState, advance });

  // Vehicle bail runs first on the temp-bail path, driver-first on direct
  const phasesOrder =
    caseRecord.bailPath === BAIL_PATH.TEMP_BAIL
      ? ["vehicle", "driver"]
      : ["driver", "vehicle"];

  const phaseSteps = { driver: driverSteps, vehicle: vehicleSteps };
  const phaseDone = (key) => phaseSteps[key].every((s) => s.done);
  const phaseActive = (key) =>
    phaseSteps[key].some((s) => !s.done) &&
    phasesOrder.slice(0, phasesOrder.indexOf(key)).every(phaseDone);

  const defaultTab = phasesOrder.find((k) => !phaseDone(k)) || phasesOrder[0];
  const [selectedTab, setSelectedTab] = useState(defaultTab);
  useEffect(() => {
    setSelectedTab(defaultTab);
  }, [defaultTab]);

  // Step 5: one-time fork right after POA generation.
  if (caseRecord.caseStep === CASE_STEP.POA_GENERATED && !caseRecord.bailPath) {
    return (
      <div className="wd-card" style={{ padding: 20, textAlign: "center" }}>
        <p style={{ fontSize: 13, marginBottom: 12, color: "var(--wd-t2)" }}>
          Was temporary bail granted for the driver?
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button
            className="wd-btn wd-btn-primary wd-btn-sm"
            onClick={() =>
              advance(CASE_STEP.TEMP_BAIL_GRANTED, {
                bailPath: BAIL_PATH.TEMP_BAIL,
              })
            }
          >
            Yes — Temporary Bail
          </button>
          <button
            className="wd-btn wd-btn-ghost wd-btn-sm"
            onClick={() =>
              advance(CASE_STEP.DRIVER_BAIL_PETITION_FILED, {
                bailPath: BAIL_PATH.DIRECT,
              })
            }
          >
            No — Proceed Directly
          </button>
        </div>
      </div>
    );
  }

  const phases = phasesOrder.map((key) => ({
    key,
    label: BAIL_PHASE_META[key].label,
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {caseRecord.bailPath === BAIL_PATH.TEMP_BAIL && (
        <div
          style={{
            padding: "8px 14px",
            borderRadius: 8,
            fontSize: 12,
            background: "rgba(34,197,94,.06)",
            border: "1px solid rgba(34,197,94,.18)",
            color: "var(--wd-green)",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          ✓ Temporary bail granted for driver
        </div>
      )}

      <PhaseTabBar
        phases={phases}
        selected={selectedTab}
        onSelect={setSelectedTab}
        isDone={phaseDone}
        isActive={phaseActive}
      />

      <BailPhaseContent steps={phaseSteps[selectedTab]} />

      {caseRecord.caseStep === CASE_STEP.CASE_CLOSED && (
        <div
          className="wd-card"
          style={{
            padding: 20,
            textAlign: "center",
            background: "rgba(34,197,94,.06)",
            border: "1px solid rgba(34,197,94,.2)",
          }}
        >
          <span style={{ fontSize: 24 }}>✅</span>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "var(--wd-green)",
              marginTop: 6,
            }}
          >
            Case Closed
          </div>
        </div>
      )}
    </div>
  );
}

function useDriverBailSteps({ caseRecord, stepState, advance }) {
  const uploadBailDoc = useUploadBailDocument(caseRecord.caseNo);
  const driverBail = caseRecord.driverBail || {};

  const [driverPetition, setDriverPetition] = useState(null);
  const [ioReport, setIoReport] = useState(null);

  // ── Police verification ──────────────────────────────────────────────
  const [docsVerified, setDocsVerified] = useState(
    driverBail.docsVerifiedByPolice?.verified ?? null,
  );
  const [verifiedAt, setVerifiedAt] = useState(
    driverBail.docsVerifiedByPolice?.at || "",
  );
  const policeState = stepState(CASE_STEP.DRIVER_DOCS_VERIFIED_BY_POLICE);
  const [editingPolice, setEditingPolice] = useState(policeState !== "done");

  const cancelEditPolice = () => {
    setDocsVerified(driverBail.docsVerifiedByPolice?.verified ?? null);
    setVerifiedAt(driverBail.docsVerifiedByPolice?.at || "");
    setEditingPolice(false);
  };

  // ── Order created ─────────────────────────────────────────────────────
  const [orderDt, setOrderDt] = useState(driverBail.orderCreatedAt || "");
  const orderState = stepState(CASE_STEP.DRIVER_ORDER_CREATED);
  const [editingOrder, setEditingOrder] = useState(orderState !== "done");

  const cancelEditOrder = () => {
    setOrderDt(driverBail.orderCreatedAt || "");
    setEditingOrder(false);
  };

  // ── Bailer details ────────────────────────────────────────────────────
  const [bailerForm, setBailerForm] = useState(
    driverBail.bailer || { name: "", mobile: "" },
  );
  const bailerState = stepState(CASE_STEP.BAILER_DETAILS_ADDED);
  const [editingBailer, setEditingBailer] = useState(bailerState !== "done");

  const cancelEditBailer = () => {
    setBailerForm(driverBail.bailer || { name: "", mobile: "" });
    setEditingBailer(false);
  };

  const nextAfterDriverDone =
    caseRecord.bailPath === BAIL_PATH.TEMP_BAIL
      ? CASE_STEP.CASE_CLOSED
      : CASE_STEP.VEHICLE_BAIL_PETITION_FILED;
  const save = (extra, nextStep) =>
    advance(nextStep || caseRecord.caseStep, extra);

  const petitionState = stepState(CASE_STEP.DRIVER_BAIL_PETITION_FILED);
  const ioState = stepState(CASE_STEP.IO_REPORT_UPLOADED);
  const doneState = stepState(CASE_STEP.DRIVER_BAIL_DONE);

  const handlePetitionSave = async (isEdit) => {
    if (!driverPetition) return;
    await uploadBailDoc.mutateAsync({
      docKey: "driverPetition",
      file: driverPetition,
    });
    setDriverPetition(null);
    if (!isEdit) advance(CASE_STEP.DRIVER_DOCS_VERIFIED_BY_POLICE);
  };
  const handleIoReportSave = async (isEdit) => {
    if (!ioReport) return;
    await uploadBailDoc.mutateAsync({ docKey: "ioReport", file: ioReport });
    setIoReport(null);
    if (!isEdit) advance(CASE_STEP.DRIVER_ORDER_CREATED);
  };

  return [
    {
      key: "petition",
      stepNo: 7,
      label: "Driver Bail Petition Filed",
      done: petitionState === "done",
      component: (
        <StepCard state={petitionState === "done" ? "done" : "warning"}>
          <StepHeader
            icon="📝"
            title="Driver Bail Petition Filed"
            subtitle="Advocate files driver bail petition; upload the copy received by email"
            badge={petitionState === "done" ? "Done" : "Action required"}
            badgeColor={
              petitionState === "done" ? "var(--wd-green)" : "var(--wd-orange)"
            }
          />
          <div style={{ marginTop: 12 }}>
            <FileDropZone
              label="Petition copy"
              file={driverPetition}
              existingUrl={driverBail.driverPetitionUrl}
              onFile={setDriverPetition}
              onClear={() => setDriverPetition(null)}
              dragId="driver-petition-input"
            />
            <div
              style={{
                marginTop: 10,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="wd-btn wd-btn-primary wd-btn-sm"
                disabled={!driverPetition || uploadBailDoc.isPending}
                onClick={() => handlePetitionSave(petitionState === "done")}
              >
                {uploadBailDoc.isPending
                  ? "Uploading…"
                  : petitionState === "done"
                    ? "Replace file"
                    : "Save & Continue"}
              </button>
            </div>
          </div>
        </StepCard>
      ),
    },
    {
      key: "police",
      stepNo: "7b",
      label: "Documents Verified by Police",
      done: policeState === "done",
      component: (
        <StepCard state={policeState === "done" ? "done" : "warning"}>
          <StepHeader
            icon="👮"
            title="Documents Verified by Police"
            subtitle="Did police verify the documents?"
            badge={policeState === "done" ? "Done" : "Action required"}
            badgeColor={
              policeState === "done" ? "var(--wd-green)" : "var(--wd-orange)"
            }
          />
          {policeState === "done" && !editingPolice ? (
            <StepSummary
              text={`${docsVerified ? "Verified" : "Not verified"}${verifiedAt ? ` · ${new Date(verifiedAt).toLocaleString()}` : ""}`}
              onEdit={() => setEditingPolice(true)}
            />
          ) : (
            <div
              style={{
                marginTop: 12,
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                className={`wd-btn wd-btn-sm ${docsVerified === true ? "wd-btn-primary" : "wd-btn-ghost"}`}
                onClick={() => setDocsVerified(true)}
              >
                Yes
              </button>
              <button
                className={`wd-btn wd-btn-sm ${docsVerified === false ? "wd-btn-primary" : "wd-btn-ghost"}`}
                onClick={() => setDocsVerified(false)}
              >
                No
              </button>
              {docsVerified === true && (
                <input
                  className="wd-input"
                  type="datetime-local"
                  value={verifiedAt}
                  onChange={(e) => setVerifiedAt(e.target.value)}
                />
              )}
              <button
                className="wd-btn wd-btn-primary wd-btn-sm"
                disabled={docsVerified == null || (docsVerified && !verifiedAt)}
                onClick={() => {
                  save(
                    {
                      driverBail: {
                        docsVerifiedByPolice: {
                          verified: docsVerified,
                          at: verifiedAt || null,
                        },
                      },
                    },
                    policeState === "done"
                      ? null
                      : CASE_STEP.IO_REPORT_UPLOADED,
                  );
                  setEditingPolice(false);
                }}
              >
                {policeState === "done" ? "Save changes" : "Save"}
              </button>
              {policeState === "done" && (
                <button
                  className="wd-btn wd-btn-ghost wd-btn-sm"
                  onClick={cancelEditPolice}
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </StepCard>
      ),
    },
    {
      key: "io_report",
      stepNo: 8,
      label: "IO Report",
      done: ioState === "done",
      component: (
        <StepCard state={ioState === "done" ? "done" : "warning"}>
          <StepHeader
            icon="📋"
            title="IO Report"
            subtitle="Upload the Investigating Officer's report"
            badge={ioState === "done" ? "Done" : "Action required"}
            badgeColor={
              ioState === "done" ? "var(--wd-green)" : "var(--wd-orange)"
            }
          />
          <div style={{ marginTop: 12 }}>
            <FileDropZone
              label="IO report"
              file={ioReport}
              existingUrl={driverBail.ioReportUrl}
              onFile={setIoReport}
              onClear={() => setIoReport(null)}
              dragId="io-report-input"
            />
            <div
              style={{
                marginTop: 10,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="wd-btn wd-btn-primary wd-btn-sm"
                disabled={!ioReport || uploadBailDoc.isPending}
                onClick={() => handleIoReportSave(ioState === "done")}
              >
                {uploadBailDoc.isPending
                  ? "Uploading…"
                  : ioState === "done"
                    ? "Replace file"
                    : "Save & Continue"}
              </button>
            </div>
          </div>
        </StepCard>
      ),
    },
    {
      key: "order",
      stepNo: 9,
      label: "Order Created",
      done: orderState === "done",
      component: (
        <StepCard state={orderState === "done" ? "done" : "warning"}>
          <StepHeader
            icon="⚖️"
            title="Order Created"
            subtitle="Record date/time the court order was created"
            badge={orderState === "done" ? "Done" : "Action required"}
            badgeColor={
              orderState === "done" ? "var(--wd-green)" : "var(--wd-orange)"
            }
          />
          {orderState === "done" && !editingOrder ? (
            <StepSummary
              text={orderDt ? new Date(orderDt).toLocaleString() : "—"}
              onEdit={() => setEditingOrder(true)}
            />
          ) : (
            <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
              <input
                className="wd-input"
                type="datetime-local"
                value={orderDt}
                onChange={(e) => setOrderDt(e.target.value)}
              />
              <button
                className="wd-btn wd-btn-primary wd-btn-sm"
                disabled={!orderDt}
                onClick={() => {
                  save(
                    { driverBail: { orderCreatedAt: orderDt } },
                    orderState === "done"
                      ? null
                      : CASE_STEP.BAILER_DETAILS_ADDED,
                  );
                  setEditingOrder(false);
                }}
              >
                {orderState === "done" ? "Save changes" : "Save & Continue"}
              </button>
              {orderState === "done" && (
                <button
                  className="wd-btn wd-btn-ghost wd-btn-sm"
                  onClick={cancelEditOrder}
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </StepCard>
      ),
    },
    {
      key: "bailer",
      stepNo: "9b",
      label: "Bailer Details",
      done: bailerState === "done",
      component: (
        <StepCard state={bailerState === "done" ? "done" : "warning"}>
          <StepHeader
            icon="🧑"
            title="Bailer Details"
            subtitle="Enter the bailer's name and mobile number"
            badge={bailerState === "done" ? "Done" : "Action required"}
            badgeColor={
              bailerState === "done" ? "var(--wd-green)" : "var(--wd-orange)"
            }
          />
          {bailerState === "done" && !editingBailer ? (
            <StepSummary
              text={`${bailerForm.name || "—"} · ${bailerForm.mobile || "—"}`}
              onEdit={() => setEditingBailer(true)}
            />
          ) : (
            <div
              style={{
                marginTop: 12,
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <input
                className="wd-input"
                placeholder="Bailer name"
                value={bailerForm.name}
                onChange={(e) =>
                  setBailerForm((f) => ({ ...f, name: e.target.value }))
                }
              />
              <input
                className="wd-input"
                placeholder="Bailer mobile"
                value={bailerForm.mobile}
                onChange={(e) =>
                  setBailerForm((f) => ({
                    ...f,
                    mobile: e.target.value.replace(/\D/g, "").slice(0, 10),
                  }))
                }
              />
              <button
                className="wd-btn wd-btn-primary wd-btn-sm"
                disabled={!bailerForm.name || bailerForm.mobile.length !== 10}
                onClick={() => {
                  save(
                    { driverBail: { bailer: bailerForm } },
                    bailerState === "done" ? null : CASE_STEP.DRIVER_BAIL_DONE,
                  );
                  setEditingBailer(false);
                }}
              >
                {bailerState === "done" ? "Save changes" : "Save"}
              </button>
              {bailerState === "done" && (
                <button
                  className="wd-btn wd-btn-ghost wd-btn-sm"
                  onClick={cancelEditBailer}
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </StepCard>
      ),
    },
    {
      key: "driver_done",
      stepNo: 10,
      label: "Driver Bail Done",
      done: doneState === "done",
      component: (
        <StepCard state={doneState === "done" ? "done" : "warning"}>
          <StepHeader
            icon="✅"
            title="Driver Bail Done"
            subtitle="Mark driver bail as complete"
            badge={doneState === "done" ? "Done" : "Action required"}
            badgeColor={
              doneState === "done" ? "var(--wd-green)" : "var(--wd-orange)"
            }
          />
          {doneState !== "done" && (
            <div style={{ marginTop: 12 }}>
              <button
                className="wd-btn wd-btn-primary wd-btn-sm"
                onClick={() => advance(nextAfterDriverDone)}
              >
                ✓ Confirm Driver Bail Done
              </button>
            </div>
          )}
        </StepCard>
      ),
    },
  ];
}

function useVehicleBailSteps({ caseRecord, stepState, advance }) {
  const uploadBailDoc = useUploadBailDocument(caseRecord.caseNo);
  const vehicleBail = caseRecord.vehicleBail || {};

  const [vehiclePetition, setVehiclePetition] = useState(null);
  const [releaseOrder, setReleaseOrder] = useState(null);

  const [orderDt, setOrderDt] = useState(vehicleBail.orderCreatedAt || "");
  const orderState = stepState(CASE_STEP.VEHICLE_ORDER_CREATED);
  const [editingOrder, setEditingOrder] = useState(orderState !== "done");

  const cancelEditOrder = () => {
    setOrderDt(vehicleBail.orderCreatedAt || "");
    setEditingOrder(false);
  };

  const nextAfterHandover =
    caseRecord.bailPath === BAIL_PATH.TEMP_BAIL
      ? CASE_STEP.DRIVER_BAIL_PETITION_FILED
      : CASE_STEP.CASE_CLOSED;
  const save = (extra, nextStep) =>
    advance(nextStep || caseRecord.caseStep, extra);

  const petitionState = stepState(CASE_STEP.VEHICLE_BAIL_PETITION_FILED);
  const releaseState = stepState(CASE_STEP.VEHICLE_RELEASE_ORDER);
  const bailDoneState = stepState(CASE_STEP.VEHICLE_BAIL_DONE);
  const handoverState = stepState(CASE_STEP.VEHICLE_HANDOVER_DONE);

  const handlePetitionSave = async (isEdit) => {
    if (!vehiclePetition) return;
    await uploadBailDoc.mutateAsync({
      docKey: "vehiclePetition",
      file: vehiclePetition,
    });
    setVehiclePetition(null);
    if (!isEdit) {
      advance(CASE_STEP.VEHICLE_ORDER_CREATED, {
        vehicleBail: { petitionFiledAt: new Date().toISOString() },
      });
    }
  };

  const handleReleaseOrderSave = async (isEdit) => {
    if (!releaseOrder) return;
    await uploadBailDoc.mutateAsync({
      docKey: "releaseOrder",
      file: releaseOrder,
    });
    setReleaseOrder(null);
    if (!isEdit) advance(CASE_STEP.VEHICLE_BAIL_DONE);
  };

  return [
    {
      key: "v_petition",
      stepNo: "11a",
      label: "Vehicle Bail Petition Filed",
      done: petitionState === "done",
      component: (
        <StepCard state={petitionState === "done" ? "done" : "warning"}>
          <StepHeader
            icon="📝"
            title="Vehicle Bail Petition Filed"
            subtitle="Advocate files the vehicle bail petition"
            badge={petitionState === "done" ? "Done" : "Action required"}
            badgeColor={
              petitionState === "done" ? "var(--wd-green)" : "var(--wd-orange)"
            }
          />
          <div style={{ marginTop: 12 }}>
            <FileDropZone
              label="Vehicle petition copy"
              file={vehiclePetition}
              existingUrl={vehicleBail.vehiclePetitionUrl}
              onFile={setVehiclePetition}
              onClear={() => setVehiclePetition(null)}
              dragId="vehicle-petition-input"
            />
            <div
              style={{
                marginTop: 10,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="wd-btn wd-btn-primary wd-btn-sm"
                disabled={!vehiclePetition || uploadBailDoc.isPending}
                onClick={() => handlePetitionSave(petitionState === "done")}
              >
                {uploadBailDoc.isPending
                  ? "Uploading…"
                  : petitionState === "done"
                    ? "Replace file"
                    : "Save & Continue"}
              </button>
            </div>
          </div>
        </StepCard>
      ),
    },
    {
      key: "v_order",
      stepNo: "11b",
      label: "Order Created",
      done: orderState === "done",
      component: (
        <StepCard state={orderState === "done" ? "done" : "warning"}>
          <StepHeader
            icon="⚖️"
            title="Order Created"
            subtitle="Court order created for vehicle release"
            badge={orderState === "done" ? "Done" : "Action required"}
            badgeColor={
              orderState === "done" ? "var(--wd-green)" : "var(--wd-orange)"
            }
          />
          {orderState === "done" && !editingOrder ? (
            <StepSummary
              text={orderDt ? new Date(orderDt).toLocaleString() : "—"}
              onEdit={() => setEditingOrder(true)}
            />
          ) : (
            <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
              <input
                className="wd-input"
                type="datetime-local"
                value={orderDt}
                onChange={(e) => setOrderDt(e.target.value)}
              />
              <button
                className="wd-btn wd-btn-primary wd-btn-sm"
                disabled={!orderDt}
                onClick={() => {
                  save(
                    { vehicleBail: { orderCreatedAt: orderDt } },
                    orderState === "done"
                      ? null
                      : CASE_STEP.VEHICLE_RELEASE_ORDER,
                  );
                  setEditingOrder(false);
                }}
              >
                {orderState === "done" ? "Save changes" : "Save & Continue"}
              </button>
              {orderState === "done" && (
                <button
                  className="wd-btn wd-btn-ghost wd-btn-sm"
                  onClick={cancelEditOrder}
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </StepCard>
      ),
    },
    {
      key: "v_release",
      stepNo: "11c",
      label: "Release Order",
      done: releaseState === "done",
      component: (
        <StepCard state={releaseState === "done" ? "done" : "warning"}>
          <StepHeader
            icon="📄"
            title="Release Order"
            subtitle="Upload the vehicle release order"
            badge={releaseState === "done" ? "Done" : "Action required"}
            badgeColor={
              releaseState === "done" ? "var(--wd-green)" : "var(--wd-orange)"
            }
          />
          <div style={{ marginTop: 12 }}>
            <FileDropZone
              label="Release order"
              file={releaseOrder}
              existingUrl={vehicleBail.releaseOrderUrl}
              onFile={setReleaseOrder}
              onClear={() => setReleaseOrder(null)}
              dragId="release-order-input"
            />
            <div
              style={{
                marginTop: 10,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="wd-btn wd-btn-primary wd-btn-sm"
                disabled={!releaseOrder || uploadBailDoc.isPending}
                onClick={() => handleReleaseOrderSave(releaseState === "done")}
              >
                {uploadBailDoc.isPending
                  ? "Uploading…"
                  : releaseState === "done"
                    ? "Replace file"
                    : "Save & Continue"}
              </button>
            </div>
          </div>
        </StepCard>
      ),
    },
    {
      key: "v_done",
      stepNo: "11d",
      label: "Vehicle Bail Done",
      done: bailDoneState === "done",
      component: (
        <StepCard state={bailDoneState === "done" ? "done" : "warning"}>
          <StepHeader
            icon="✅"
            title="Vehicle Bail Done"
            subtitle="Confirm the vehicle bail is finalised"
            badge={bailDoneState === "done" ? "Done" : "Action required"}
            badgeColor={
              bailDoneState === "done" ? "var(--wd-green)" : "var(--wd-orange)"
            }
          />
          {bailDoneState !== "done" && (
            <div style={{ marginTop: 12 }}>
              <button
                className="wd-btn wd-btn-primary wd-btn-sm"
                onClick={() => advance(CASE_STEP.VEHICLE_HANDOVER_DONE)}
              >
                ✓ Confirm Vehicle Bail Done
              </button>
            </div>
          )}
        </StepCard>
      ),
    },
    {
      key: "v_handover",
      stepNo: "11e",
      label: "Vehicle Handover",
      done: handoverState === "done",
      component: (
        <StepCard state={handoverState === "done" ? "done" : "warning"}>
          <StepHeader
            icon="🚚"
            title="Vehicle Handover"
            subtitle="Confirm vehicle handed back to customer"
            badge={handoverState === "done" ? "Done" : "Action required"}
            badgeColor={
              handoverState === "done" ? "var(--wd-green)" : "var(--wd-orange)"
            }
          />
          {handoverState !== "done" && (
            <div style={{ marginTop: 12 }}>
              <button
                className="wd-btn wd-btn-primary wd-btn-sm"
                onClick={() => advance(nextAfterHandover)}
              >
                ✓ Confirm Handover
              </button>
            </div>
          )}
        </StepCard>
      ),
    },
  ];
}

/* ══════════════════ Timeline ══════════════════ */

function TimelineTab({ caseRecord }) {
  const list = caseRecord.timeline || [];
  if (list.length === 0) {
    return (
      <div
        className="wd-card"
        style={{ padding: 32, textAlign: "center", color: "var(--wd-t3)" }}
      >
        No history yet.
      </div>
    );
  }
  return (
    <div className="wd-card" style={{ padding: 24 }}>
      {[...list].reverse().map((e, i) => (
        <div key={e.id ?? i} style={{ display: "flex", gap: 14 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "var(--wd-blue)",
                flexShrink: 0,
                marginTop: 4,
              }}
            />
            {i < list.length - 1 && <div className="wd-tl-line" />}
          </div>
          <div style={{ paddingBottom: 20 }}>
            <span
              style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-t1)" }}
            >
              {(e.status || "").replace(/_/g, " ")}
            </span>
            {e.description && (
              <p
                style={{
                  margin: "3px 0 0",
                  fontSize: 12,
                  color: "var(--wd-t2)",
                }}
              >
                {e.description}
              </p>
            )}
            <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 3 }}>
              {/* CHANGED: real timeline rows use actor/timestamp, not by/at */}
              {e.actor || e.role} · {new Date(e.timestamp).toLocaleString()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

